// Global 401 guard.
//
// A backend 401 means the bearer token this tab is holding is no longer
// accepted (it expired, or the user logged out / a different user logged in on
// the auth server in another tab). We patch window.fetch once so that ANY API
// call getting a 401 triggers a single trip through the OAuth flow, which
// reloads whoever is signed in right now (or the login page if nobody is).
//
// - Requests to the auth server itself are ignored (the token exchange must be
//   allowed to fail without looping).
// - A loop guard stops us bouncing repeatedly if a 401 keeps coming back right
//   after a successful re-auth (backend down, clock skew, ...): we surface a
//   "something went wrong" toast instead.
// - After the re-auth completes we show a toast ONLY if the signed-in user
//   actually changed - a plain token expiry for the same user is silent.

import { authServer, appUrl } from "./app_props";
import { clearAuth, ensureFreshSession } from "./authStorage";

const REAUTH_TS_KEY = "authGuardReauthAt";
const CHANGED_FLAG_KEY = "authGuardSessionChanged";
const PREV_USER_KEY = "authGuardPrevUser";
const LOOP_WINDOW_MS = 15000;

let installed = false;

const urlOf = (input) => {
  try {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
  } catch (e) {
    /* ignore */
  }
  return "";
};

const isAuthServerRequest = (input) => {
  const url = urlOf(input);
  return (
    (authServer && url.indexOf(authServer) === 0) || url.indexOf("/oauth2/") !== -1
  );
};

const currentUsername = () => {
  try {
    return JSON.parse(sessionStorage.getItem("userInfo") || "{}").username || "";
  } catch (e) {
    return "";
  }
};

const handleUnauthorized = () => {
  // Already handling the OAuth callback - let it finish.
  if (window.location.pathname.indexOf("/authorized") !== -1) return;

  const last = Number(sessionStorage.getItem(REAUTH_TS_KEY) || 0);
  if (Date.now() - last < LOOP_WINDOW_MS) {
    // We just re-authed and it 401'd again - this is not an identity problem.
    window.dispatchEvent(new CustomEvent("authGuard:error"));
    return;
  }

  try {
    sessionStorage.setItem(REAUTH_TS_KEY, String(Date.now()));
    localStorage.setItem(CHANGED_FLAG_KEY, "1");
    localStorage.setItem(PREV_USER_KEY, currentUsername());
  } catch (e) {
    /* ignore */
  }

  clearAuth();
  // ensureFreshSession stashes the current deep path and redirects to the app
  // root; if we are already at the root it returns false and we reload it so
  // <Redirect> re-runs the OAuth handshake.
  if (!ensureFreshSession(appUrl)) window.location.replace(appUrl);
};

// ---- toast -----------------------------------------------------------------
const showToast = (message) => {
  try {
    if (document.getElementById("auth-guard-toast")) return;
    const el = document.createElement("div");
    el.id = "auth-guard-toast";
    el.textContent = message;
    el.style.cssText = [
      "position:fixed",
      "top:16px",
      "left:50%",
      "transform:translateX(-50%)",
      "z-index:2147483647",
      "background:#323232",
      "color:#fff",
      "padding:10px 16px",
      "border-radius:6px",
      "font:500 13px/1.4 Roboto,Arial,sans-serif",
      "box-shadow:0 4px 16px rgba(0,0,0,.25)",
      "max-width:90vw",
      "cursor:pointer",
    ].join(";");
    el.addEventListener("click", () => el.remove());
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  } catch (e) {
    /* ignore */
  }
};

const flushChangedToast = () => {
  try {
    if (!localStorage.getItem(CHANGED_FLAG_KEY)) return true; // nothing pending
    if (!sessionStorage.getItem("id_token")) return false; // not signed in yet
    localStorage.removeItem(CHANGED_FLAG_KEY);
    const prev = localStorage.getItem(PREV_USER_KEY) || "";
    localStorage.removeItem(PREV_USER_KEY);
    const now = currentUsername();
    if (prev && now && prev !== now) {
      showToast("Your session changed - please try that action again.");
    }
    return true;
  } catch (e) {
    return true;
  }
};

export const installFetchAuthGuard = () => {
  if (installed || typeof window === "undefined" || !window.fetch) return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (...args) =>
    nativeFetch(...args).then((res) => {
      try {
        if (res && res.status === 401 && !isAuthServerRequest(args[0])) {
          handleUnauthorized();
        }
      } catch (e) {
        /* never break fetch */
      }
      return res;
    });

  window.addEventListener("authGuard:error", () =>
    showToast("Something went wrong. Please try again.")
  );

  // The signed-in user may only appear after the OAuth exchange (a client-side
  // navigation, not a full load), so poll briefly for the pending toast.
  if (!flushChangedToast()) {
    let tries = 0;
    const iv = setInterval(() => {
      if (flushChangedToast() || ++tries > 60) clearInterval(iv);
    }, 1000);
  }
};
